import i18n from 'i18n-js'
import { useCallback, useRef, useState } from 'react'
import { RECIPIENT_TYPE, REPORT_OPTION } from '../../../../utils/enum'
import { isEmail, isPhoneNumber } from '../../../../validators/validator'

/**
 * Hook controller del Report Modal.
 * - Mantiene estado: open, origin, recipient, relativeEmail, relativePhone, loading, snackbar.
 * - Permite registrar handlers por (origin, recipient_type); al enviar se invoca el handler con el payload.
 */
export const useReportModalController = () => {
  const [open, setOpen] = useState(false)
  const [origin, setOrigin] = useState(null)
  const [recipient, setRecipient] = useState(null)
  const [relativeEmail, setRelativeEmail] = useState('')
  const [relativePhone, setRelativePhone] = useState('')
  const [loading, setLoading] = useState(false)
  const [snackbar, setSnackbar] = useState({ text: '', severity: 'info', show: false })

  const handlersRef = useRef({})
  const extraPayloadRef = useRef({})

  const registerHandler = useCallback((orig, recipientType, fn) => {
    if (!handlersRef.current[orig]) handlersRef.current[orig] = {}
    handlersRef.current[orig][recipientType] = fn
  }, [])

  const openModal = useCallback((orig, extraPayload = {}) => {
    setOrigin(orig)
    setRecipient(null)
    setRelativeEmail('')
    setRelativePhone('')
    extraPayloadRef.current = extraPayload
    setOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    setOpen(false)
    setOrigin(null)
    setRecipient(null)
    setRelativeEmail('')
    setRelativePhone('')
    setLoading(false)
  }, [])

  const handleSelectRecipient = useCallback((value) => {
    setRecipient(value)
    setRelativeEmail('')
    setRelativePhone('')
  }, [])

  const handleRelativeEmailChange = useCallback((value) => {
    setRelativeEmail(value)
  }, [])

  const handleRelativePhoneChange = useCallback((value) => {
    setRelativePhone(value)
  }, [])

  const handleSubmit = useCallback(
    async () => {
      if (!recipient) {
        setSnackbar({ text: i18n.t('patient.detail.reportModal.recipientRequired'), severity: 'warning', show: true })
        return
      }
      if (recipient === RECIPIENT_TYPE.RELATIVE && (!relativeEmail || !relativePhone)) {
        setSnackbar({ text: i18n.t('patient.detail.reportModal.relativeContactRequired'), severity: 'warning', show: true })
        return
      }
      if (recipient === RECIPIENT_TYPE.RELATIVE && relativeEmail && isEmail(relativeEmail)) {
        setSnackbar({ text: i18n.t('patient.detail.reportModal.relativeEmailInvalid'), severity: 'warning', show: true })
        return
      }
      if (recipient === RECIPIENT_TYPE.RELATIVE && relativePhone && !isPhoneNumber(relativePhone)) {
        setSnackbar({ text: i18n.t('patient.detail.reportModal.relativePhoneInvalid'), severity: 'warning', show: true })
        return
      }

      const fn = handlersRef.current[origin]?.[recipient]
      const payload = {
        origin,
        recipient_type: recipient,
        relative_email: relativeEmail || undefined,
        relative_phone: relativePhone || undefined,
        ...extraPayloadRef.current,
      }
      setLoading(true)
      try {
        if (typeof fn === 'function') {
          const result = await fn(payload)
          if (result && typeof result === 'object' && result.text) {
            setSnackbar({ text: result.text, severity: result.severity || 'success', show: true })
          } else {
            setSnackbar({
              text: i18n.t('patient.detail.reportModal.success'),
              severity: 'success',
              show: true,
            })
          }
        } else {
          setSnackbar({
            text: i18n.t('patient.detail.reportModal.comingSoon'),
            severity: 'info',
            show: true,
          })
        }
      } catch (e) {
        setSnackbar({
          text: e?.message || i18n.t('patient.detail.reportModal.error'),
          severity: 'warning',
          show: true,
        })
      } finally {
        setLoading(false)
        closeModal()
      }
    },
    [origin, recipient, relativeEmail, relativePhone, closeModal]
  )

  const handleDownloadClick = useCallback(
    async () => {
      if (origin == null) return
      const fn = handlersRef.current[origin]?.[REPORT_OPTION.DOWNLOAD]
      const payload = { origin, optionId: REPORT_OPTION.DOWNLOAD, ...extraPayloadRef.current }
      setLoading(true)
      try {
        if (typeof fn === 'function') {
          const result = await fn(payload)
          if (result && typeof result === 'object' && result.text) {
            setSnackbar({ text: result.text, severity: result.severity || 'success', show: true })
          } else {
            setSnackbar({
              text: i18n.t('patient.detail.reportModal.success'),
              severity: 'success',
              show: true,
            })
          }
        } else {
          setSnackbar({
            text: i18n.t('patient.detail.reportModal.comingSoon'),
            severity: 'info',
            show: true,
          })
        }
      } catch (e) {
        setSnackbar({
          text: e?.message || i18n.t('patient.detail.reportModal.error'),
          severity: 'warning',
          show: true,
        })
      } finally {
        setLoading(false)
      }
    },
    [origin]
  )

  const handleSnackBarClose = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, show: false }))
  }, [])

  return {
    open,
    origin,
    recipient,
    relativeEmail,
    relativePhone,
    loading,
    snackbar,
    openModal,
    closeModal,
    handleSelectRecipient,
    handleRelativeEmailChange,
    handleRelativePhoneChange,
    handleSubmit,
    handleDownloadClick,
    handleSnackBarClose,
    registerHandler,
  }
}
